/**
 * ProfileChecklistSheet (web): setup checklist dialog.
 *
 * Web sibling of the native checklist sheet, modeled on the sheet-family
 * scaffold (ProfileSheetScaffold.web) with the row/progress pattern of
 * CvSetupSheet: progress track, n/total + percent header, check indicators,
 * and rows that navigate to the exact relevant form.
 *
 * Works for both modes: individual rows keep their historical labels; company
 * rows read the server-derived checklist (PRD §11) and the company copy.
 */
import React, { useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { getPublicProfileChecklistRoutes } from '../../lib/public-profile-routes';
import { ProfileSheetScaffold } from './ProfileSheetScaffold';

const FONT_STACK = "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif";

export function ProfileChecklistSheet() {
  const { t } = useTranslation('common');
  const router = useRouter();

  const { checklistItems, checklistDone, checklistTotal, completionPercent, routeMode } =
    usePublicProfileBuilderModel();
  const checklistRoutes = useMemo(
    () => getPublicProfileChecklistRoutes(routeMode),
    [routeMode],
  );

  const tx = (key: string) => String(t(key as never));
  const labelFor = (id: string) =>
    tx(
      routeMode === 'company'
        ? `publicProfile.company.checklist.${id}`
        : `publicProfile.checklist.items.${id}`,
    );

  return (
    <ProfileSheetScaffold
      title={tx('publicProfile.checklist.title')}
      subtitle={`${checklistDone}/${checklistTotal} · ${completionPercent}%`}
      compact
    >
      {/* Progress track */}
      <div
        style={{
          height: 6,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${completionPercent}%`,
            borderRadius: 999,
            background: '#5e29ff',
            transition: 'width 240ms ease',
          }}
        />
      </div>

      <div
        style={{
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 20,
          overflow: 'hidden',
        }}
      >
        {checklistItems.map((item, index) => (
          <ChecklistRow
            key={item.id}
            label={labelFor(item.id)}
            completed={item.completed}
            isLast={index === checklistItems.length - 1}
            onPress={() => {
              const route = checklistRoutes[item.id];
              if (route) {
                router.push(route as never);
              }
            }}
          />
        ))}
      </div>
    </ProfileSheetScaffold>
  );
}

function ChecklistRow({
  label,
  completed,
  isLast,
  onPress,
}: {
  label: string;
  completed: boolean;
  isLast: boolean;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minHeight: 54,
        padding: '12px 16px',
        boxSizing: 'border-box',
        cursor: 'pointer',
        background: hover ? 'rgba(0,0,0,0.04)' : 'transparent',
        borderBottom: isLast ? 'none' : '1px solid var(--gem-outline-variant)',
        transition: 'background-color 140ms',
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: 12,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          border: completed ? '2px solid #5e29ff' : '2px solid var(--input-border)',
          background: completed ? '#5e29ff' : 'transparent',
          color: '#fff',
        }}
      >
        {completed ? <MaterialSymbol name="check" size={15} /> : null}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 16,
          lineHeight: '20px',
          color: completed ? 'var(--text-tertiary)' : 'var(--text-primary)',
          textDecoration: completed ? 'line-through' : 'none',
          textAlign: 'start',
          fontFamily: FONT_STACK,
        }}
      >
        {label}
      </span>
      <span className="tabbio-rtl-flip" style={{ display: 'inline-flex', color: 'var(--text-tertiary)' }}>
        <MaterialSymbol name="chevron_right" size={18} />
      </span>
    </button>
  );
}
